import React, { useEffect, useRef, useState } from "react";
import { Form, Input } from "semantic-ui-react";
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useAuth } from "../../context/authContext";
import "../Login/Login.css";
import { ROUTES } from "../../constants";
import { useTranslation } from "react-i18next";
import { sendOTP, verifyEmail } from "../../services/verifyEmailService";
import { AuthPageStyles } from "../../utilities/themeColors";
import CompanyLogo from "../../components/CompanyLogo/CompanyLogo";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const VerifyEmail = () => {
	const [error, setError] = useState(""); // General error message
	const [loading, setLoading] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [resendLoading, setResendLoading] = useState(false);
	const [navLoading, setNavLoading] = useState(false);
	const { logout, user, updateUserStatus } = useAuth();
	const [otp, setOtp] = useState("");
	const navigate = useNavigate();
	const {t} = useTranslation();
	const { authBackdropStyle } = useBrandingAssets();
	const autoOtpKey = user?.user_id ? `verify_email_otp_auto_sent_at_${user.user_id}` : "";
	const initialAutoSendInFlightRef = useRef(false);

	const combinedErrorMessage = [error].filter(Boolean).join(" | ");

	useEffect(() => {
		const handleBackNavigation = async () => {
			await logout();
		};

		window.history.pushState({ verifyEmail: true }, "", window.location.href);
		window.addEventListener("popstate", handleBackNavigation);

		return () => {
			window.removeEventListener("popstate", handleBackNavigation);
		};
	}, [logout]);

	useEffect(() => {
		const triggerInitialOtp = async () => {
			if (!autoOtpKey || !user?.user_id) return;
			const lastAutoSentAt = Number(sessionStorage.getItem(autoOtpKey) || 0);
			if (Number.isFinite(lastAutoSentAt) && (Date.now() - lastAutoSentAt) < 15000) return;
			if (initialAutoSendInFlightRef.current) return;
			initialAutoSendInFlightRef.current = true;
			try {
				const response = await sendOTP({ user_id: user.user_id }, { forceResend: true });
				if (!response?.status) {
					throw new Error(response?.message || "Failed to send OTP");
				}
				sessionStorage.setItem(autoOtpKey, String(Date.now()));
			} catch (err) {
				initialAutoSendInFlightRef.current = false;
				console.error("Failed to send initial OTP on Verify Email page:", err);
				setError(err?.response?.data?.message || err?.message || "Failed to send OTP");
				return;
			}
		};
		triggerInitialOtp();
	}, [autoOtpKey, user?.user_id]);

	const handleBackToLogin = async (e) => {
		e.preventDefault();
		await logout();
	};

	const handleVerifyEmail = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		try {
			const response = await verifyEmail(otp, { user_id: user.user_id });
			if (response.status) {
				setSuccessMessage("Email verified successfully");
				updateUserStatus({ email_verified: true, email_verified_date: new Date(), first_login: false });
			} else if (response.message === "Email already verified") {
				setSuccessMessage("Email Already verified successfully");
				updateUserStatus({ email_verified: true, email_verified_date: new Date(), first_login: false });
			} else {
				throw new Error(response?.message || "Failed to Verify Email");
			}
			setNavLoading(true);
			setTimeout(() => {
				navigate("/");
				setNavLoading(false);
			}, 1000);
		} catch (error) {
			setError(error?.response?.data?.message || error?.message || "Failed to Verify Email");
		} finally {
			setLoading(false);
		}
	};

	const handleResendOtp = async () => {
		setError("");
		setResendLoading(true);
		try {
			const response = await sendOTP({ user_id: user.user_id }, { forceResend: true });
			if (response.status) {
				setSuccessMessage("OTP sent to your email address");
				setResendLoading(false);
			} else {
				throw new Error(response?.message || "Failed to resend OTP");
			}
		} catch (error) {
			setError(error?.response?.data?.message || error?.message || "Failed to resend OTP");
			setResendLoading(false);
		}
		setTimeout(() => {
			setSuccessMessage("");
		}, 3000);
	};
	return (
		<Box className="login-container" sx={authBackdropStyle || AuthPageStyles}>
			<Box className="login-form-container">
				<CompanyLogo alt="Logo" className="login-logo" />
				<div className="form-container">
					<PageTitle title={t('TITLES.VERIFY_EMAIL')} />

					{combinedErrorMessage && (
						<Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{combinedErrorMessage}
						</Alert>
					)}
					{successMessage && successMessage !== "" && (
						<Alert severity="success" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{successMessage}
						</Alert>
					)}
					<Form className="login-form" onSubmit={handleVerifyEmail} error={!!combinedErrorMessage}>
						<Form.Field error={!!error}>
							<Input
								fluid
								placeholder={t('PLACEHOLDER.ENTER_OTP')}
								type="tel"
								className="login-input"
								value={otp}
								onChange={(e) => setOtp(e.target.value)}
							/>
						</Form.Field>
						<Box mt={2} mb={2} textAlign="center">
							<Typography variant="body1" textAlign="center" color="text.secondary" fontSize={12}>
								{t('UI_TEXT.OTP_SEND_EMAIL')}
								<Link
									onClick={handleResendOtp}
									className="login-link"
									style={{ fontSize: "12px", marginLeft: "5px" }}>
									{t('UI_TEXT.RESEND_OTP')}
								</Link>
							</Typography>
						</Box>
						<div className="login-button">
							<Button
								variant="contained"
								color="primary"
								className="button-primary"
								type="submit"
								sx={{ minWidth: "120px" }}
								disabled={loading}>
								{loading ? "Verifying..." : "Verify"}
							</Button>
						</div>
						{resendLoading && (
							<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
								<CircularProgress size={24} sx={{ color: "green" }} />
								<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
									{t('UI_TEXT.RESENDING_OTP')}
								</Typography>
							</Box>
						)}
						{navLoading && (
							<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
								<CircularProgress size={24} sx={{ color: "green" }} />
								<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
									{t('UI_TEXT.NAVIGATE_HOMEPAGE')}
								</Typography>
							</Box>
						)}
					</Form>
					<Typography variant="body2" color="textSecondary" textAlign="center" marginTop="15px">
						<Link to={ROUTES.LOGIN} className="login-link" onClick={handleBackToLogin}>
							{t('UI_TEXT.BACK_TO_LOGIN')}
						</Link>
					</Typography>
				</div>
			</Box>
		</Box>
	);
};

export default VerifyEmail;
